// NotesPanel.js
import React, { useState, useEffect, useCallback } from "react";
import { Plus, Save, Trash2, FileText, X } from "lucide-react";
import api from "./api";
import ReactMarkdown from "react-markdown";
import "./NotesPanel.css";
import { isInternalRepoNote } from "./repoContextConstants";

const NotesPanel = ({ repoId, onNotesChange }) => {
  const [notes, setNotes] = useState([]);
  const [selectedNote, setSelectedNote] = useState(null);
  const [content, setContent] = useState("");
  const [filePath, setFilePath] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [isNewNote, setIsNewNote] = useState(false);

  const loadNotes = useCallback(async () => {
    if (!repoId) return;
    
    try {
      setLoading(true);
      setError("");
      const response = await api.getNotes(repoId);
      setNotes(response.data.notes || []);
    } catch (err) {
      console.error("Failed to load notes:", err);
      setError(err.response?.data?.error || "Failed to load notes");
    } finally {
      setLoading(false);
    }
  }, [repoId]);

  // Load notes on mount and when repoId changes
  useEffect(() => {
    if (repoId) {
      loadNotes();
    }
  }, [repoId, loadNotes]);

  const handleNewNote = () => {
    setSelectedNote(null);
    setContent("");
    setFilePath(null);
    setIsNewNote(true);
    setError("");
  };

  const handleSelectNote = (note) => {
    setSelectedNote(note);
    setContent(note.content || "");
    setFilePath(note.filePath || null);
    setIsNewNote(false);
    setError("");
  };

  const handleSave = async () => {
    if (!content.trim()) {
      setError("Content cannot be empty");
      return;
    }

    if (!repoId) {
      setError("Repository ID is missing");
      return;
    }

    try {
      setSaving(true);
      setError("");

      if (isNewNote) {
        // Create new note
        const response = await api.createNote(repoId, {
          content: content.trim(),
          filePath: filePath || null,
        });
        await loadNotes();
        handleSelectNote(response.data.note);
        setIsNewNote(false);
        if (onNotesChange) onNotesChange();
      } else if (selectedNote) {
        // Update existing note
        await api.updateNote(repoId, selectedNote._id, {
          content: content.trim(),
          filePath: filePath || null,
        });
        await loadNotes();
        // Refresh selected note
        const updatedNote = notes.find(n => n._id === selectedNote._id) || selectedNote;
        handleSelectNote({ ...updatedNote, content: content.trim(), filePath: filePath || null });
        if (onNotesChange) onNotesChange();
      }
    } catch (err) {
      console.error("Failed to save note:", err);
      setError(err.response?.data?.error || "Failed to save note");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedNote || !repoId) return;

    if (!window.confirm("Are you sure you want to delete this note?")) {
      return;
    }

    try {
      setSaving(true);
      setError("");
      await api.deleteNote(repoId, selectedNote._id);
      await loadNotes();
      setSelectedNote(null);
      setContent("");
      setFilePath(null);
      setIsNewNote(false);
      if (onNotesChange) onNotesChange();
    } catch (err) {
      console.error("Failed to delete note:", err);
      setError(err.response?.data?.error || "Failed to delete note");
    } finally {
      setSaving(false);
    }
  };

  // Group notes by filePath (hide internal sentinel notes used by Codebase Guide)
  const visibleNotes = notes.filter((n) => !isInternalRepoNote(n));
  const repoLevelNotes = visibleNotes.filter((n) => !n.filePath);
  const fileNotesMap = new Map();
  visibleNotes.filter((n) => n.filePath).forEach((note) => {
    if (!fileNotesMap.has(note.filePath)) {
      fileNotesMap.set(note.filePath, []);
    }
    fileNotesMap.get(note.filePath).push(note);
  });

  return (
    <div className="notes-panel">
      <div className="notes-panel-layout">
        {/* Left: Notes List */}
        <div className="notes-list">
          <div className="notes-list-header">
            <h3>Notes</h3>
            <button
              className="btn-new-note"
              onClick={handleNewNote}
              title="New note"
            >
              <Plus size={16} />
              New Note
            </button>
          </div>

          {loading && <div className="notes-loading">Loading notes...</div>}

          {!loading && visibleNotes.length === 0 && (
            <div className="notes-empty">
              <p>No notes yet. Create your first note!</p>
            </div>
          )}

          {!loading && visibleNotes.length > 0 && (
            <div className="notes-list-content">
              {/* Repo-level notes */}
              {repoLevelNotes.length > 0 && (
                <div className="notes-group">
                  <div className="notes-group-header">Repository Notes</div>
                  {repoLevelNotes.map((note) => (
                    <div
                      key={note._id}
                      className={`note-item ${selectedNote?._id === note._id ? "active" : ""}`}
                      onClick={() => handleSelectNote(note)}
                    >
                      <div className="note-item-content">
                        <div className="note-item-preview">
                          {note.content.substring(0, 100)}
                          {note.content.length > 100 && "..."}
                        </div>
                        <div className="note-item-meta">
                          {new Date(note.updatedAt).toLocaleDateString()}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* File-level notes */}
              {fileNotesMap.size > 0 && (
                <div className="notes-group">
                  <div className="notes-group-header">File Notes</div>
                  {Array.from(fileNotesMap.entries()).map(([path, pathNotes]) => (
                    <div key={path} className="notes-file-group">
                      <div className="notes-file-header">
                        <FileText size={14} />
                        <span className="notes-file-path">{path}</span>
                      </div>
                      {pathNotes.map((note) => (
                        <div
                          key={note._id}
                          className={`note-item ${selectedNote?._id === note._id ? "active" : ""}`}
                          onClick={() => handleSelectNote(note)}
                        >
                          <div className="note-item-content">
                            <div className="note-item-preview">
                              {note.content.substring(0, 80)}
                              {note.content.length > 80 && "..."}
                            </div>
                            <div className="note-item-meta">
                              {new Date(note.updatedAt).toLocaleDateString()}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Right: Editor */}
        <div className="notes-editor">
          <div className="notes-editor-header">
            <h3>{isNewNote ? "New Note" : selectedNote ? "Edit Note" : "No Note Selected"}</h3>
            {selectedNote && !isNewNote && (
              <button
                className="btn-delete-note"
                onClick={handleDelete}
                disabled={saving}
                title="Delete note"
              >
                <Trash2 size={16} />
              </button>
            )}
          </div>

          {error && <div className="notes-error">{error}</div>}

          {(selectedNote || isNewNote) ? (
            <div className="notes-editor-content">
              <div className="notes-editor-field">
                <label>File Path (optional, leave empty for repo-level note)</label>
                <input
                  type="text"
                  value={filePath || ""}
                  onChange={(e) => setFilePath(e.target.value || null)}
                  placeholder="e.g., src/components/Button.js"
                  disabled={saving}
                />
              </div>

              <div className="notes-editor-field">
                <label>Content (Markdown supported)</label>
                <div className="notes-editor-split">
                  <div className="notes-editor-textarea">
                    <textarea
                      value={content}
                      onChange={(e) => setContent(e.target.value)}
                      placeholder="Enter your note content here... Markdown is supported."
                      disabled={saving}
                      rows={15}
                    />
                  </div>
                  <div className="notes-editor-preview">
                    <div className="notes-preview-label">Preview</div>
                    <div className="notes-preview-content">
                      <ReactMarkdown>{content || "*No content yet*"}</ReactMarkdown>
                    </div>
                  </div>
                </div>
              </div>

              <div className="notes-editor-actions">
                <button
                  className="btn-save-note"
                  onClick={handleSave}
                  disabled={saving || !content.trim()}
                >
                  <Save size={16} />
                  {saving ? "Saving..." : "Save"}
                </button>
                {!isNewNote && (
                  <button
                    className="btn-cancel-note"
                    onClick={() => {
                      setSelectedNote(null);
                      setContent("");
                      setFilePath(null);
                      setIsNewNote(false);
                      setError("");
                    }}
                    disabled={saving}
                  >
                    <X size={16} />
                    Cancel
                  </button>
                )}
              </div>
            </div>
          ) : (
            <div className="notes-editor-empty">
              <p>Select a note from the list or create a new one to get started.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default NotesPanel;
